import { useEffect } from "react";
import { fetchStats, requestAvatarMessage } from "@/lib/api";
import { fireMochiNotification } from "@/lib/mochiNotifications";

const NOTIF_INTERVALS = {
    gentle:         4 * 60 * 60 * 1000,  // 4 hours
    balanced:       90 * 60 * 1000,      // 90 minutes
    accountability: 2 * 60 * 60 * 1000,  // 2 hours — explicit "ping me to finish a task"
};
const MIN_GAP_MS = 25 * 60 * 1000;       // never pile on within 25 min of any other nudge
const TICK_MS = 5 * 60 * 1000;           // poll every 5 min so snoozes feel responsive

export function pickAccountabilityTask(tasks, excludeId) {
    if (!Array.isArray(tasks) || tasks.length === 0) return undefined;
    const undone = tasks.filter((t) => !t.completed && t.id !== excludeId);
    if (undone.length === 0) return undefined;
    const score = (t) => {
        let s = 0;
        if (t.priority === "high") s += 100;
        else if (t.priority === "medium") s += 30;
        if (t.due_time) s += 20;
        return s;
    };
    return [...undone]
        .sort((a, b) => score(b) - score(a) || (a.created_at || "").localeCompare(b.created_at || ""))[0];
}

// Periodically checks if Mochi should ping the user. Respects mode
// (gentle/balanced/accountability), the 25-min anti-pile-on gap with
// `lastCheckinRef`, the user-controlled `nextEligibleAtRef` snooze gate,
// and the 22:00–07:00 quiet window. In accountability mode, surfaces a
// task-specific nag every 2h until the popup is dismissed or snoozed.
//
//   usePeriodicCheckin({ settings, tasks, lastCheckinRef, nextEligibleAtRef, onCheckin });
export function usePeriodicCheckin({ settings, tasks, lastCheckinRef, nextEligibleAtRef, onCheckin }) {
    useEffect(() => {
        if (!settings) return;
        const mode = settings.notification_mode || "balanced";
        const interval = NOTIF_INTERVALS[mode] || NOTIF_INTERVALS.balanced;

        const tick = async () => {
            const now = Date.now();
            if (nextEligibleAtRef && now < nextEligibleAtRef.current) return;
            if (now - lastCheckinRef.current < MIN_GAP_MS) return;

            const hour = new Date().getHours();
            if (hour < 7 || hour >= 22) return;

            const fresh = await fetchStats().catch(() => null);
            if (!fresh) return;

            const undone =
                (fresh.tasks_total - fresh.tasks_done) +
                (fresh.habits_total - fresh.habits_done) +
                (fresh.reminders_total - fresh.reminders_done);

            if (fresh.tasks_total + fresh.habits_total + fresh.reminders_total === 0) return;
            if (undone === 0 && mode !== "accountability") return;

            const isAccountabilityNag = mode === "accountability" && undone > 0;
            const task = isAccountabilityNag ? pickAccountabilityTask(tasks) : undefined;

            const event = isAccountabilityNag
                ? "accountability_ping"
                : undone === 0
                    ? "all_done"
                    : "checkin";

            const context = {
                tasks_done: fresh.tasks_done, tasks_total: fresh.tasks_total,
                habits_done: fresh.habits_done, habits_total: fresh.habits_total,
            };
            if (task?.title) context.task_title = task.title;

            try {
                const r = await requestAvatarMessage(event, fresh.emotion, context);
                const fired = Date.now();
                lastCheckinRef.current = fired;
                if (nextEligibleAtRef) nextEligibleAtRef.current = fired + interval;
                onCheckin?.({
                    message: r.message,
                    mood: fresh.emotion,
                    kind: isAccountabilityNag ? "accountability" : undefined,
                    taskId: task?.id,
                });
                if ("Notification" in window && Notification.permission === "granted") {
                    // Map (mode, accountability) → phrase bank type. Title is
                    // always "mochi 🍡" inside the dispatcher.
                    let notifType;
                    if (isAccountabilityNag) notifType = "accountability_ping";
                    else if (mode === "gentle") notifType = "checkin_gentle";
                    else if (mode === "accountability") notifType = "checkin_accountability";
                    else notifType = "checkin_balanced";
                    fireMochiNotification(notifType, task?.title ? { task_title: task.title } : {});
                }
            } catch (e) { console.warn("periodic check-in failed", e); }
        };

        const id = setInterval(tick, TICK_MS);
        return () => clearInterval(id);
    }, [settings, tasks, lastCheckinRef, nextEligibleAtRef, onCheckin]);
}
